import { webDataErrorSimple, webDataLoading, WebDataSteps, webDataSuccess } from "@lvt/web-data";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { DateTime } from "luxon";

import { PresetImagesService } from "@/api/services/interfaces";
import { RootState } from "@/store";
import { setBucketImages } from "@/store/archives/slice";
import { hourInSeconds } from "@/utilities/time";
import { ArchiveImage, Preset, PresetsToImages } from "@/utilities/types/archive";


export interface PresetFetchParams {
    presets: Preset[];
    startTime: DateTime;
    endTime: DateTime;
    presetImagesService: PresetImagesService;
}

// TODO: how to do testing for this "interface"?
// TODO: how to limit/type check the dispatch?
export const fetchPresetImages = createAsyncThunk<void, PresetFetchParams, { state: RootState }>(
    "presetImages/fetchByIdsStatus",
    async ({ presets, presetImagesService, startTime, endTime }, { dispatch, getState }) => {
        // get start and stop
        const startTimeSeconds = startTime.toSeconds();
        const startBucket = startTimeSeconds - (startTimeSeconds % hourInSeconds);
        const endTimeSeconds = endTime.toSeconds();
        const endBucket = endTimeSeconds - (endTimeSeconds % hourInSeconds);

        const state = getState();
        const images = state.archives.images;

        const promises: Promise<void>[] = [];

        let currentBucket = startBucket;

        while (currentBucket <= endBucket) {
            const bucket = currentBucket;
            const imageBucket = images[bucket];
            const presetsToFetch: number[] = [];

            presets.forEach((preset) => {
                if (!imageBucket || !imageBucket[preset.id]) {
                    presetsToFetch.push(preset.id);
                } else if (imageBucket && imageBucket[preset.id] && imageBucket[preset.id].type == WebDataSteps.Error) {
                    presetsToFetch.push(preset.id);
                }
            });

            // To give an immutable copy to the promise
            const presetsToFetchCopy = [...presetsToFetch];

            if (presetsToFetchCopy.length > 0) {
                const presetsToImagesInit: PresetsToImages = {};
                const presetsToImages = presetsToFetch.reduce((sofar, presetId) => {
                    sofar[presetId] = webDataLoading();

                    return sofar;
                }, presetsToImagesInit);

                dispatch(setBucketImages({ presetsToImages, startTime: bucket }));

                promises.push(
                    presetImagesService
                        .fetchPresetsImages(
                            presetsToFetchCopy,
                            DateTime.fromSeconds(bucket),
                            DateTime.fromSeconds(bucket + hourInSeconds),
                        )
                        .then((response) => {
                            // isError doesn't work here as the axios error apparently doesn't always have 'name'
                            if (!Array.isArray(response)) {
                                const presetsToErrorImagesInit: PresetsToImages = {};
                                const presetsToErrorImages = presetsToFetch.reduce((sofar, presetId) => {
                                    sofar[presetId] = webDataErrorSimple({
                                        message: "An error occurred while fetching images",
                                    });

                                    return sofar;
                                }, presetsToErrorImagesInit);

                                dispatch(setBucketImages({ presetsToImages: presetsToErrorImages, startTime: bucket }));
                            } else {
                                const presetsToImagesInit: Record<number, ArchiveImage[]> = {};
                                // Force there to be at least something so it resolves to a success of [] if nothing returned
                                const presetsToImagesPrimed: Record<number, ArchiveImage[]> = presetsToFetchCopy.reduce(
                                    (sofar, presetId) => {
                                        if (!sofar[presetId]) {
                                            sofar[presetId] = [];
                                        }

                                        return sofar;
                                    },
                                    presetsToImagesInit,
                                );

                                // Add in any images we might of found
                                const presetsToImages: Record<number, ArchiveImage[]> = response.reduce(
                                    (sofar, next) => {
                                        if (!sofar[next.presetId]) {
                                            sofar[next.presetId] = [];
                                        }

                                        sofar[next.presetId] = [...sofar[next.presetId], next];

                                        return sofar;
                                    },
                                    presetsToImagesPrimed,
                                );

                                // Convert to webdatas for dispatch
                                const presetsToWebDataImagesInit: PresetsToImages = {};
                                const presetsToWebDataImages = Object.entries(presetsToImages).reduce(
                                    (sofar, [presetIdStr, images]) => {
                                        const base = 10;
                                        const presetId = parseInt(presetIdStr, base);

                                        sofar[presetId] = webDataSuccess(images);

                                        return sofar;
                                    },
                                    presetsToWebDataImagesInit,
                                );

                                // Send it!
                                dispatch(
                                    setBucketImages({
                                        presetsToImages: presetsToWebDataImages,
                                        startTime: bucket,
                                    }),
                                );
                            }
                        }),
                );
            }

            currentBucket += hourInSeconds;
        }
    },
);
